import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
} from 'amazon-cognito-identity-js'

// Promise wrapper around amazon-cognito-identity-js (USER_SRP_AUTH flow).
// The pool uses email as an *alias*, so usernames must not be email-shaped:
// sign-up generates one, and confirm/resend need that generated username.

const userPool = new CognitoUserPool({
  UserPoolId: process.env.VUE_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.VUE_APP_COGNITO_CLIENT_ID,
})

const PENDING_KEY = 'cognitoPendingSignUp'

function cognitoUser (username) {
  return new CognitoUser({ Username: username, Pool: userPool })
}

function callback (resolve, reject) {
  return (err, result) => (err ? reject(err) : resolve(result))
}

function generateUsername () {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
  return 'u' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10)
}

// --- sign in / out ---------------------------------------------------------

export function signIn (email, password) {
  const user = cognitoUser(email.trim())
  const details = new AuthenticationDetails({ Username: email.trim(), Password: password })
  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: session => resolve({ session }),
      onFailure: reject,
      // Admin-created users must set a password on first login.
      newPasswordRequired: () => resolve({ newPasswordRequired: true, user }),
    })
  })
}

export function completeNewPassword (user, newPassword) {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: session => resolve({ session }),
      onFailure: reject,
    })
  })
}

export function signOut ({ global = false } = {}) {
  const user = userPool.getCurrentUser()
  if (!user) return Promise.resolve()
  if (!global) {
    user.signOut()
    return Promise.resolve()
  }
  // Global sign-out needs a valid session; fall back to a local sign-out.
  return getSession()
    .then(() => new Promise(resolve => user.globalSignOut({ onSuccess: resolve, onFailure: resolve })))
    .finally(() => user.signOut())
}

// --- session / tokens ------------------------------------------------------

// Resolves the current session (refreshing it if expired) or null when signed out.
export function getSession () {
  const user = userPool.getCurrentUser()
  if (!user) return Promise.resolve(null)
  return new Promise(resolve => {
    user.getSession((err, session) => resolve(!err && session && session.isValid() ? session : null))
  })
}

export async function isAuthenticated () {
  return (await getSession()) !== null
}

export async function getIdToken () {
  const session = await getSession()
  return session ? session.getIdToken().getJwtToken() : null
}

export async function getCurrentUserInfo () {
  const session = await getSession()
  if (!session) return null
  const claims = session.getIdToken().decodePayload()
  return {
    username: claims['cognito:username'],
    sub: claims.sub,
    email: claims.email,
    name: claims.name,
  }
}

// --- sign up ---------------------------------------------------------------

export function signUp ({ email, password, name }) {
  const username = generateUsername()
  const attributes = [new CognitoUserAttribute({ Name: 'email', Value: email.trim() })]
  if (name) attributes.push(new CognitoUserAttribute({ Name: 'name', Value: name.trim() }))
  return new Promise((resolve, reject) => {
    userPool.signUp(username, password, attributes, null, callback(resolve, reject))
  }).then(result => {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ username, email: email.trim() }))
    return { username, confirmed: result.userConfirmed, destination: result.codeDeliveryDetails }
  })
}

export function getPendingSignUp () {
  try {
    return JSON.parse(sessionStorage.getItem(PENDING_KEY)) || null
  } catch (e) {
    return null
  }
}

export function confirmSignUp (username, code) {
  return new Promise((resolve, reject) => {
    // forceAliasCreation=false: never steal an email already used by a confirmed account.
    cognitoUser(username).confirmRegistration(code.trim(), false, callback(resolve, reject))
  }).then(result => {
    sessionStorage.removeItem(PENDING_KEY)
    return result
  })
}

export function resendSignUpCode (username) {
  return new Promise((resolve, reject) => {
    cognitoUser(username).resendConfirmationCode(callback(resolve, reject))
  })
}

// --- password reset --------------------------------------------------------

export function forgotPassword (email) {
  return new Promise((resolve, reject) => {
    cognitoUser(email.trim()).forgotPassword({ onSuccess: resolve, onFailure: reject })
  })
}

export function confirmForgotPassword (email, code, newPassword) {
  return new Promise((resolve, reject) => {
    cognitoUser(email.trim()).confirmPassword(code.trim(), newPassword, {
      onSuccess: resolve,
      onFailure: reject,
    })
  })
}

export function changePassword (oldPassword, newPassword) {
  const user = userPool.getCurrentUser()
  if (!user) return Promise.reject(new Error('Not signed in'))
  return getSession().then(() => new Promise((resolve, reject) => {
    user.changePassword(oldPassword, newPassword, callback(resolve, reject))
  }))
}

// --- errors ----------------------------------------------------------------

const MESSAGES = {
  NotAuthorizedException: 'Incorrect email or password.',
  UserNotFoundException: 'Incorrect email or password.',
  UserNotConfirmedException: 'Your account is not verified yet. Check your email for the code.',
  UsernameExistsException: 'An account with this email already exists.',
  AliasExistsException: 'An account with this email already exists.',
  CodeMismatchException: 'That code is not correct.',
  ExpiredCodeException: 'That code has expired. Request a new one.',
  InvalidPasswordException: 'Password must be at least 8 characters and include upper and lower case letters, a number and a symbol.',
  LimitExceededException: 'Too many attempts. Please wait a few minutes and try again.',
  TooManyRequestsException: 'Too many attempts. Please wait a few minutes and try again.',
}

export function authErrorMessage (err) {
  if (!err) return 'Something went wrong.'
  return MESSAGES[err.code || err.name] || err.message || 'Something went wrong.'
}
